<template>
  <section class="invoice-add-wrapper">
    <validation-observer ref="simpleRules">
      <b-form>
        <b-row>
          <!-- ============================================ -->
          <!-- PANEL IZQUIERDO — Datos del pedido -->
          <!-- ============================================ -->
          <b-col
            cols="12"
            md="4"
            xl="3"
          >
            <div class="order-add-wrapper order-header">
              <!-- ======================== Cliente ======================== -->
              <div class="order-header__client">
                <label
                  class="order-header__label"
                  for="select-customers"
                >Cliente</label>
                <validation-provider
                  v-slot="{ errors }"
                  name="cliente"
                  rules="required"
                >
                  <b-form-group class="mb-0">
                    <v-select
                      v-if="availableCustomers"
                      ref="select"
                      input-id="select-customers"
                      placeholder="Selecciona un cliente"
                      :options="availableCustomers"
                      :label="customerSelectLabel"
                      :clearable="false"
                      class="order-header__customer-select"
                      @input="val => setCustomerData(val)"
                      @search="searchCustomers"
                    >
                      <template #open-indicator="{ attributes }">
                        <feather-icon
                          v-bind="attributes"
                          icon="SearchIcon"
                          size="16"
                        />
                      </template>
                      <template #list-header>
                        <li
                          class="add-new-customer-header d-flex align-items-center my-50"
                          @click="customerAddSidebarActive = true"
                        >
                          <feather-icon
                            icon="PlusIcon"
                            size="16"
                          />
                          <span class="align-middle ml-25">Nuevo cliente</span>
                        </li>
                      </template>
                      <template #option="option">
                        {{ option.nit }} - {{ option.label }}
                      </template>
                      <template slot="no-options">
                        No se encontraron clientes
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>

                <template v-if="customerData.id">
                  <!-- Ficha del cliente -->
                  <div class="order-header__customer">
                    <span class="order-header__avatar">
                      <feather-icon
                        icon="UserIcon"
                        size="22"
                      />
                    </span>
                    <div class="order-header__customer-body">
                      <h5 class="order-header__customer-name">
                        #{{ customerData.id }} - {{ customerData.name }}
                      </h5>
                      <div class="order-header__meta">
                        <span>
                          <span class="text-muted">NIT:</span>
                          {{ (customerData.nit_cf === 'Y') ? `CF - ${customerData.nit}` : customerData.nit }}
                        </span>
                        <span>
                          <span class="text-muted">Cel:</span>
                          {{ customerData.cellphone || '-' }}
                        </span>
                        <span>
                          <span class="text-muted">Tel:</span>
                          {{ customerData.phone || '-' }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Direccion -->
                  <div class="order-header__address">
                    <feather-icon
                      icon="MapPinIcon"
                      size="16"
                      class="order-header__address-icon"
                    />
                    <span class="text-muted mr-50">Dirección:</span>
                    <div class="order-header__address-body">
                      <template v-if="customerData.addresses && customerData.addresses.length > 0">
                        <template v-if="selectedAddress">
                          <b>{{ selectedAddress.name }}:</b>
                          {{ selectedAddress.address }}, {{ selectedAddress.municipality }}, {{ selectedAddress.department }}
                          <template v-if="selectedAddress.reference">
                            <br><b>Ref:</b> {{ selectedAddress.reference }}
                          </template>
                          <div>
                            <a
                              href="#"
                              class="order-header__link"
                              @click.prevent="showAddressSelector = true"
                            >
                              <feather-icon
                                icon="ListIcon"
                                size="14"
                                class="mr-25"
                              />Ver más direcciones
                            </a>
                          </div>
                        </template>
                        <a
                          v-else
                          href="#"
                          class="order-header__link"
                          @click.prevent="showAddressSelector = true"
                        >
                          <feather-icon
                            icon="MapIcon"
                            size="14"
                            class="mr-25"
                          />Seleccionar dirección
                        </a>
                      </template>
                      <a
                        v-else
                        href="#"
                        class="order-header__link"
                        @click.prevent="openAddressSidebar"
                      >
                        <feather-icon
                          icon="PlusCircleIcon"
                          size="16"
                          class="mr-25"
                        />Agregar dirección
                      </a>
                    </div>
                  </div>
                </template>
              </div>

              <!-- ======================== Detalles del pedido ======================== -->
              <div
                v-if="customerData.id"
                class="order-header__details"
              >
                <h5 class="order-header__title">
                  Detalles del pedido
                </h5>

                <b-row>
                  <b-col
                    cols="12"
                    lg="6"
                  >
                    <validation-provider
                      v-slot="{ errors }"
                      name="fecha"
                      rules="required"
                    >
                      <b-form-group
                        label="Fecha"
                        label-for="order-date"
                        label-class="order-header__label"
                      >
                        <b-form-datepicker
                          id="order-date"
                          v-model="customerOrderData.order_date"
                          :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                          locale="es"
                          placeholder="Fecha"
                          selected-variant="warning"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col
                    cols="12"
                    lg="6"
                  >
                    <b-form-group
                      label="Lista de precios"
                      label-class="order-header__label"
                    >
                      <v-select
                        v-model="customerOrderData.price_list_id"
                        :options="priceLists"
                        label="name"
                        :reduce="priceList => priceList.id"
                        placeholder="Lista"
                        :disabled="!isAdmin || orderLines.length > 0"
                        class="order-header__price-list"
                        @input="onPriceListChange"
                      />
                      <small
                        v-if="!isAdmin"
                        class="text-muted"
                      >
                        Es la lista asignada al cliente y define el precio de venta.
                      </small>
                      <small
                        v-else-if="orderLines.length > 0"
                        class="text-warning"
                      >
                        Elimina los productos para cambiar la lista
                      </small>
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    lg="6"
                  >
                    <b-form-group
                      label="Moneda"
                      label-class="order-header__label"
                    >
                      <v-select
                        v-model="customerOrderData.currency_id"
                        :options="currencies"
                        label="name"
                        :reduce="currency => currency.id"
                        placeholder="Moneda"
                      >
                        <template #option="option">
                          {{ option.name }} {{ option.symbol }}
                        </template>
                      </v-select>
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    lg="6"
                  >
                    <b-form-group
                      label="Tipo de venta"
                      label-for="order-origin"
                      label-class="order-header__label"
                    >
                      <b-form-select
                        id="order-origin"
                        v-model="customerOrderData.origin_sale"
                        :options="saleOriginOptions"
                      />
                    </b-form-group>
                  </b-col>

                  <!-- Vendedor (solo admin) -->
                  <b-col
                    v-if="userData().role === 'admin'"
                    cols="12"
                  >
                    <validation-provider
                      v-slot="{ errors }"
                      name="vendedor"
                      rules="required"
                    >
                      <b-form-group
                        label="Vendedor"
                        label-class="order-header__label"
                      >
                        <v-select
                          v-if="vendorsOptions"
                          :options="vendorsOptions"
                          label="label"
                          placeholder="Selecciona un vendedor"
                          @search="searchVendors"
                          @input="val => customerOrderData.user_id = val ? val.id : null"
                        >
                          <template #option="option">
                            {{ option.id }} - {{ option.label }}
                          </template>
                          <template slot="no-options">
                            No se encontraron vendedores
                          </template>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12">
                    <b-form-group
                      label="Observaciones"
                      label-for="order-observations"
                      label-class="order-header__label"
                      class="mb-0"
                    >
                      <b-form-textarea
                        id="order-observations"
                        v-model="customerOrderData.observations"
                        placeholder="Comentarios del pedido"
                        rows="3"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-col>

          <!-- ============================================ -->
          <!-- PANEL DERECHO — Catálogo + Grilla -->
          <!-- ============================================ -->
          <b-col
            v-if="customerData.id"
            cols="12"
            md="8"
            xl="9"
          >
            <!-- Catálogo -->
            <b-card no-body>
              <b-card-body class="pb-50">
                <h6 class="mb-1">
                  <b>Catálogo de productos:</b>
                </h6>

                <b-form-input
                  v-model="searchQuery"
                  placeholder="Buscar por código o nombre de estilo..."
                  class="mb-1"
                />

                <div
                  v-if="!customerOrderData.price_list_id"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-warning mb-50"
                  />
                  <p class="text-muted mb-0">
                    Selecciona una lista de precios para ver el catálogo
                  </p>
                </div>

                <div
                  v-else-if="!searchQuery.trim() && estilos.length === 0"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="32"
                    class="text-muted mb-50"
                  />
                  <p class="text-muted mb-0">
                    Busca un producto por código o nombre para comenzar
                  </p>
                </div>

                <div
                  v-else-if="isLoadingProducts"
                  class="text-center py-2"
                >
                  <b-spinner
                    variant="primary"
                    class="mb-50"
                  />
                  <p class="text-muted mb-0">
                    Buscando productos...
                  </p>
                </div>

                <div
                  v-else-if="estilos.length === 0 && searchQuery.trim().length >= 2"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="32"
                    class="text-muted mb-50"
                  />
                  <p class="text-muted mb-0">
                    No se encontraron productos para "{{ searchQuery }}"
                  </p>
                </div>

                <div
                  v-else-if="estilos.length > 0"
                  class="order-add-wrapper__catalogo"
                >
                  <div
                    v-for="estilo in estilos"
                    :key="estilo.code + '-' + estilo.color"
                    class="estilo-card"
                    :class="{ 'estilo-card--added': isStyleAdded(estilo.code, estilo.color) }"
                    @click="onToggleStyle(estilo)"
                  >
                    <img
                      :src="getEstiloThumbnail(estilo)"
                      :alt="estilo.code"
                      class="estilo-card__thumb"
                      @error="onEstiloImageError"
                    >
                    <div class="estilo-card__info">
                      <span class="estilo-card__code">
                        {{ estilo.code }}
                      </span>
                      <small class="text-muted">
                        {{ estilo.color }}
                      </small>
                    </div>
                    <div class="estilo-card__action">
                      <feather-icon
                        v-if="isStyleAdded(estilo.code, estilo.color)"
                        icon="CheckCircleIcon"
                        size="20"
                        class="text-success"
                      />
                      <feather-icon
                        v-else
                        icon="PlusCircleIcon"
                        size="20"
                        class="text-primary"
                      />
                    </div>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Grilla del pedido -->
            <b-card no-body>
              <b-card-body>
                <h6 class="mb-1">
                  <b>Detalle del pedido ({{ activeLines.length }} productos):</b>
                </h6>

                <div class="order-grid">
                  <div
                    v-if="gridRows.length === 0"
                    class="text-center py-2"
                  >
                    <feather-icon
                      icon="ShoppingCartIcon"
                      size="32"
                      class="text-muted mb-1"
                    />
                    <p class="text-muted mb-0">
                      Selecciona un estilo del catálogo para agregar sus tallas
                    </p>
                  </div>

                  <div
                    v-else
                    class="table-responsive"
                  >
                    <table class="table table-sm order-grid__table">
                      <thead>
                        <tr>
                          <th class="order-grid__th-style">
                            Estilo-Color
                          </th>
                          <th
                            v-for="size in allSizes"
                            :key="size"
                            class="order-grid__th-size"
                          >
                            {{ size }}
                          </th>
                          <th class="order-grid__th-num">
                            TOTAL
                          </th>
                          <th class="order-grid__th-price">
                            Precio unit
                          </th>
                          <th class="order-grid__th-num">
                            MONEDA
                          </th>
                          <th class="order-grid__th-amount">
                            TOTAL EN {{ currencySymbol }}
                          </th>
                          <th class="order-grid__th-actions" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in gridRows"
                          :key="row.key"
                        >
                          <td class="order-grid__style-cell">
                            <span class="font-weight-bold">
                              {{ row.estilo }}
                            </span>
                            <small class="text-muted d-block">
                              {{ row.color }}
                            </small>
                          </td>
                          <td
                            v-for="size in allSizes"
                            :key="size"
                            class="order-grid__qty-cell"
                          >
                            <template v-if="row.sizeMap[size]">
                              <input
                                type="number"
                                class="order-grid__qty-input"
                                :class="{
                                  'order-grid__qty-input--max': row.sizeMap[size].cantidad >= row.sizeMap[size].stock,
                                  'order-grid__qty-input--empty': row.sizeMap[size].cantidad === 0,
                                  'order-grid__qty-input--no-stock': row.sizeMap[size].stock === 0,
                                }"
                                :value="row.sizeMap[size].cantidad || ''"
                                placeholder="0"
                                min="0"
                                :max="row.sizeMap[size].stock"
                                :disabled="row.sizeMap[size].stock === 0"
                                @change="onQtyChange(row.sizeMap[size], $event)"
                              >
                              <small class="order-grid__stock-hint">
                                {{ row.sizeMap[size].stock }}
                              </small>
                              <!-- Talla con oferta: precio de lista tachado y precio ofertado. -->
                              <div
                                v-if="lineOffer(row.sizeMap[size])"
                                class="order-grid__offer"
                                :title="`Oferta: ahorra ${currencySymbol}${lineOffer(row.sizeMap[size]).ahorro.toFixed(2)} por unidad`"
                              >
                                <s>{{ currencySymbol }}{{ lineOffer(row.sizeMap[size]).lista.toFixed(2) }}</s>
                                <strong>{{ currencySymbol }}{{ lineOffer(row.sizeMap[size]).oferta.toFixed(2) }}</strong>
                              </div>
                            </template>
                          </td>
                          <td class="text-center font-weight-bold">
                            {{ row.totalQty }}
                          </td>
                          <td>
                            <div class="price-input">
                              <span class="price-input__display price-input__display--locked">
                                {{ currencySymbol }}{{ Number(row.precioActual).toFixed(2) }}
                              </span>
                              <span
                                v-if="row.offerSizes > 0 && !row.isOffer"
                                class="price-input__badge price-input__badge--offer"
                                title="Hay tallas con precio ofertado; su precio aparece en la celda de la talla"
                              >
                                oferta en {{ row.offerSizes }} {{ row.offerSizes === 1 ? 'talla' : 'tallas' }}
                              </span>
                              <span
                                v-if="row.isOffer"
                                class="price-input__badge price-input__badge--offer"
                                title="Precio ofertado vigente; pisa el precio de la lista del cliente"
                              >
                                oferta
                              </span>
                              <span
                                v-else-if="row.isCustomPrice && !row.offerSizes"
                                class="price-input__badge"
                                title="Precio propio de la lista; no sigue al precio base del estilo"
                              >
                                lista
                              </span>
                              <span
                                v-else
                                class="price-input__original"
                                title="Esta variante sigue el precio base del estilo"
                              >
                                base
                              </span>
                            </div>
                          </td>
                          <td class="text-center text-muted">
                            {{ currencyCode }}
                          </td>
                          <td class="text-right font-weight-bold order-grid__row-total">
                            {{ currencySymbol }}{{ row.totalAmount }}
                          </td>
                          <td class="text-center">
                            <button
                              type="button"
                              class="btn btn-sm btn-flat-danger"
                              @click="onRemoveStyle(row.key)"
                            >
                              <feather-icon
                                icon="TrashIcon"
                                size="14"
                              />
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div class="order-grid__footer">
                      <span>Total:</span>
                      <span class="order-grid__grand-total">
                        {{ currencySymbol }}{{ grandTotal }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Minimo de unidades de la lista (PRECIO VOLUMEN); el servidor tambien bloquea. -->
                <price-list-unit-rule-notice
                  :customer-id="customerOrderData.customer_id"
                  :price-list-id="customerOrderData.price_list_id"
                  :units="unitRuleUnits"
                  documento="pedido"
                  @change="unitRuleOk = $event"
                />

                <b-button
                  v-ripple.400="'rgba(255, 159, 67, 0.15)'"
                  variant="primary"
                  block
                  class="mt-1"
                  :disabled="isSavingOrder || activeLines.length === 0 || !unitRuleOk"
                  @click="saveData"
                >
                  <template v-if="isSavingOrder">
                    <b-spinner
                      small
                      class="mr-50"
                    />
                    Guardando...
                  </template>
                  <template v-else>
                    Guardar pedido
                  </template>
                </b-button>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <customers-address-selector
          :show.sync="showAddressSelector"
          :customer-id="customerOrderData.customer_id"
          :selected-address-id="customerOrderData.shipping_address_id"
          @address-selected="selectAddress"
        />

        <customers-add
          :customer-add-sidebar-active.sync="customerAddSidebarActive"
          @refreshData="getCustomers"
        />
      </b-form>
    </validation-observer>

    <customers-address-form
      v-if="customerData && customerData.id"
      :show.sync="showAddressSidebar"
      :customer-id="customerData.id"
      :customer-data="customerData"
      :address-data="null"
      @refresh-data="refreshCustomerAddresses"
    />
  </section>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import { isAdmin } from '@/utils/auth'
import { lineOffer, rowPriceSummary, catalogVariant } from '../components/gridOffer'
import { orderUnits } from '../components/unitRuleStatus'
import PriceListUnitRuleNotice from '../components/PriceListUnitRuleNotice.vue'
import CustomersAdd from '../../contacts/customers/CustomersAdd.vue'
import CustomersAddressForm from '../../contacts/customers/CustomersAddressForm.vue'
import CustomersAddressSelector from '../../contacts/customers/CustomersAddressSelector.vue'

export default {
  components: {
    PriceListUnitRuleNotice,
    vSelect,
    CustomersAdd,
    ValidationProvider,
    ValidationObserver,
    CustomersAddressForm,
    CustomersAddressSelector,
  },
  directives: {
    Ripple,
  },

  data() {
    return {
      customerOrderData: {
        customer_id: '',
        user_id: this.userData().id,
        price_list_id: null,
        currency_id: null,
        order_date: this.getCurrentDate(),
        observations: '',
        origin_sale: 'Cliente Final',
        products: [],
        shipping_address_id: null,
        shipping_address_text: '',
      },

      availableCustomers: [],
      customerAddSidebarActive: false,
      customerSelectLabel: 'label',
      customerData: {},

      priceLists: [],
      currencies: [],

      allVariants: [],
      estilos: [],
      searchQuery: '',
      isLoadingProducts: false,

      orderLines: [],

      vendorsOptions: [],
      saleOriginOptions: [
        { value: 'Cliente Final', text: 'Cliente Final' },
        { value: 'Venta en línea', text: 'Venta en línea' },
      ],

      showAddressSelector: false,
      showAddressSidebar: false,
      selectedAddress: null,

      isSavingOrder: false,
      // Lo informa PriceListUnitRuleNotice: false bloquea el boton de guardar.
      unitRuleOk: true,
      timeout: null,

      isAdmin: isAdmin(),
    }
  },

  computed: {
    // Unidades para el minimo de la lista: en cotizacion, las cubiertas por
    // stock; en pedido, las del pedido (ver unitRuleStatus.js).
    unitRuleUnits() {
      return orderUnits(this.activeLines)
    },

    activeLines() {
      return this.orderLines.filter(line => line.cantidad > 0)
    },

    currencySymbol() {
      if (!this.customerOrderData.currency_id) return 'Q'
      const selected = this.currencies.find(c => c.id === this.customerOrderData.currency_id)
      return selected ? (selected.symbol || 'Q') : 'Q'
    },

    currencyCode() {
      if (!this.customerOrderData.currency_id) return 'GTQ'
      const selected = this.currencies.find(c => c.id === this.customerOrderData.currency_id)
      return selected && selected.code ? selected.code : 'GTQ'
    },

    allSizes() {
      const sizes = new Set()
      this.orderLines.forEach(line => sizes.add(line.size))
      const arr = [...sizes]
      arr.sort((a, b) => {
        const numA = parseFloat(a)
        const numB = parseFloat(b)
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
        return a.localeCompare(b)
      })
      return arr
    },

    gridRows() {
      const groups = {}

      this.orderLines.forEach(line => {
        const key = `${line.estilo}-${line.color}`
        if (!groups[key]) {
          groups[key] = {
            key,
            estilo: line.estilo,
            color: line.color,
            precioActual: line.precioActual,
            precioBase: line.precioBase,
            isCustomPrice: line.isCustomPrice,
            isOffer: line.isOffer,
            sizeMap: {},
            lines: [],
          }
        }
        groups[key].sizeMap[line.size] = line
        groups[key].lines.push(line)
      })

      return Object.values(groups).map(group => ({
        ...group,
        ...rowPriceSummary(group.lines),
        totalQty: group.lines.reduce((sum, l) => sum + l.cantidad, 0),
        totalAmount: Number(
          group.lines.reduce((sum, l) => sum + (l.precioActual * l.cantidad), 0),
        ).toFixed(2),
      }))
    },

    grandTotal() {
      return Number(
        this.orderLines.reduce((sum, l) => sum + (l.precioActual * l.cantidad), 0),
      ).toFixed(2)
    },
  },

  watch: {
    searchQuery(val) {
      if (this.timeout) clearTimeout(this.timeout)
      const query = val.trim()
      if (query.length < 2) {
        if (query.length === 0) {
          this.allVariants = []
          this.estilos = []
        }
        return
      }
      this.timeout = setTimeout(() => {
        this.searchProductsFromServer()
      }, 400)
    },
  },

  created() {
    this.getCustomers()
    this.getPriceLists()
    this.getCurrencies()
    if (this.userData().role === 'admin') {
      this.getVendors()
    }
  },

  methods: {
    lineOffer,

    userData() {
      return JSON.parse(localStorage.getItem('DuttonAuthUser'))
    },

    isStyleAdded(code, color) {
      return this.orderLines.some(l => l.estilo === code && l.color === color)
    },

    getCustomers() {
      this.availableCustomers = []
      axios
        .get('customer?perPage=10&sortField=id&sortDesc=desc&filterField=status&filterValue=Y')
        .then(response => {
          response.data.data.forEach(el => {
            this.availableCustomers.push({
              label: el.name,
              id: el.id,
              nit: el.nit,
            })
          })
        })
        .catch(error => { this.showErrors(error) })
    },

    searchCustomers(search, loading) {
      if (this.isNIT(search)) {
        this.customerSelectLabel = 'nit'
      } else {
        this.customerSelectLabel = 'label'
      }

      if (search.length) {
        loading(true)
        if (this.timeout) clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
          axios
            .get(`customer?filterField=status&filterValue=Y&query=${search}`)
            .then(response => {
              loading(false)
              this.availableCustomers = []
              response.data.data.forEach(el => {
                this.availableCustomers.push({
                  label: el.name,
                  id: el.id,
                  nit: el.nit,
                })
              })
            })
            .catch(error => { this.showErrors(error) })
        }, 300)
      } else {
        this.getCustomers()
      }
    },

    isNIT(value) {
      return /^\d+$/.test(value) || value === 'CF'
    },

    getPriceLists() {
      axios.get('price-list-active')
        .then(response => { this.priceLists = response.data.data })
        .catch(error => { this.showErrors(error) })
    },

    getCurrencies() {
      axios.get('currency-active')
        .then(response => { this.currencies = response.data.data })
        .catch(error => { this.showErrors(error) })
    },

    getVendors() {
      this.vendorsOptions = []
      const filterArray = [{ field: 'active', value: 'Y' }]
      axios
        .get(`system/users?filters=${JSON.stringify(filterArray)}&perPage=50`)
        .then(response => {
          response.data.data.forEach(el => {
            this.vendorsOptions.push({ label: el.name, id: el.id })
          })
        })
        .catch(error => { this.showErrors(error) })
    },

    searchVendors(search, loading) {
      if (search.length === 0) { loading(false); return }
      loading(true)
      const filterArray = [{ field: 'active', value: 'Y' }]
      axios
        .get(`system/users?filters=${JSON.stringify(filterArray)}&query=${search}&perPage=50`)
        .then(response => {
          const results = []
          response.data.data.forEach(el => {
            results.push({ label: el.name, id: el.id })
          })
          const all = [...this.vendorsOptions]
          results.forEach(r => {
            if (!all.find(o => o.id === r.id)) all.push(r)
          })
          this.vendorsOptions = all
          loading(false)
        })
        .catch(error => { loading(false); this.showErrors(error) })
    },

    setCustomerData(customer) {
      this.orderLines = []
      this.allVariants = []
      this.estilos = []
      this.searchQuery = ''

      if (!customer) {
        this.customerData = {}
        this.customerOrderData.customer_id = ''
        this.customerOrderData.price_list_id = null
        this.selectedAddress = null
        return
      }

      this.customerOrderData.customer_id = customer.id
      this.selectedAddress = null
      this.customerOrderData.shipping_address_id = null
      this.customerOrderData.shipping_address_text = ''
      this.customerOrderData.price_list_id = null

      axios.get(`customer/${customer.id}`)
        .then(response => {
          this.customerData = response.data.data

          if (this.customerData.price_list_id) {
            this.customerOrderData.price_list_id = this.customerData.price_list_id
          } else {
            this.makeToast('warning', 'Cliente sin lista de precios', 'Este cliente no tiene una lista de precios asignada.')
          }

          if (this.customerData.addresses && this.customerData.addresses.length > 0) {
            const defaultAddr = this.customerData.addresses.find(a => a.is_default)
            this.selectAddress(defaultAddr || this.customerData.addresses[0])
          }
        })
        .catch(error => { this.showErrors(error) })
    },

    onPriceListChange() {
      if (this.orderLines.length > 0) {
        this.$bvModal
          .msgBoxConfirm('Al cambiar la lista de precios se eliminarán los productos del pedido. ¿Continuar?', {
            title: 'Confirmar cambio',
            size: 'sm',
            okVariant: 'warning',
            cancelVariant: 'outline-dark',
            okTitle: 'SI',
            cancelTitle: 'NO',
            centered: true,
          })
          .then(confirmed => {
            if (confirmed) {
              this.orderLines = []
              this.allVariants = []
              this.estilos = []
              this.searchQuery = ''
            } else {
              this.customerOrderData.price_list_id = this.customerData.price_list_id
            }
          })
      } else {
        this.allVariants = []
        this.estilos = []
        this.searchQuery = ''
      }
    },

    searchProductsFromServer() {
      if (!this.customerOrderData.price_list_id || !this.customerOrderData.customer_id) return

      const query = this.searchQuery.trim()
      if (query.length < 2) return

      this.isLoadingProducts = true

      const params = {
        price_list_id: this.customerOrderData.price_list_id,
        customer_id: this.customerOrderData.customer_id,
        search: query,
        perPage: 50,
      }

      axios.get('product-variant-with-prices', { params })
        .then(response => {
          this.allVariants = response.data.data.data || []
          this.groupByEstilo()
          this.isLoadingProducts = false
        })
        .catch(error => {
          this.isLoadingProducts = false
          this.showErrors(error)
        })
    },

    groupByEstilo() {
      const map = {}

      this.allVariants.forEach(v => {
        const code = v.code || v.label.split('-').slice(0, -1).join('-') || v.label
        const key = `${code}-${v.color}`

        if (!map[key]) {
          map[key] = {
            code,
            description: v.descripcion || '',
            color: v.color || '',
            image: v.imagen || null,
            variants: [],
          }
        }

        map[key].variants.push(catalogVariant(v))
      })

      Object.values(map).forEach(estilo => {
        estilo.variants.sort((a, b) => {
          const numA = parseFloat(a.size)
          const numB = parseFloat(b.size)
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
          return a.size.localeCompare(b.size)
        })
      })

      this.estilos = Object.values(map)
    },

    onToggleStyle(estilo) {
      const key = `${estilo.code}-${estilo.color}`
      if (this.isStyleAdded(estilo.code, estilo.color)) {
        this.onRemoveStyle(key)
        return
      }

      estilo.variants.forEach(v => {
        const exists = this.orderLines.some(l => l.sku === v.sku)
        if (!exists) {
          this.orderLines.push({
            sku: v.sku,
            estilo: estilo.code,
            color: estilo.color,
            descripcion: v.descripcion || estilo.description,
            precioBase: v.base_price,
            precioActual: v.precio,
            precioLista: v.precio_lista,
            cantidad: 0,
            productVariantId: v.id,
            presentacion: v.presentacion,
            size: v.size,
            imagen: estilo.image,
            isCustomPrice: v.is_custom_price,
            isOffer: v.is_offer,
            stock: v.stock,
          })
        }
      })
    },

    onRemoveStyle(styleKey) {
      this.orderLines = this.orderLines.filter(
        line => `${line.estilo}-${line.color}` !== styleKey,
      )
    },

    onQtyChange(line, event) {
      const { target } = event
      const val = parseInt(target.value, 10)
      let qty
      if (Number.isNaN(val) || val < 0) {
        target.value = 0
        qty = 0
      } else {
        qty = Math.min(val, line.stock)
        target.value = qty || ''
      }
      const stored = this.orderLines.find(l => l.sku === line.sku)
      if (stored) stored.cantidad = qty
    },

    getEstiloThumbnail(estilo) {
      if (estilo.image && estilo.image.path) {
        return buildStorageUrl(estilo.image.path)
      }
      return NO_IMAGE_URL
    },

    onEstiloImageError(e) {
      e.target.src = NO_IMAGE_URL
    },

    openAddressSidebar() {
      this.showAddressSidebar = true
      this.showAddressSelector = false
    },

    selectAddress(address) {
      if (!address) return
      this.selectedAddress = address
      this.customerOrderData.shipping_address_id = address.id
      this.customerOrderData.shipping_address_text = `${address.name}: ${address.address} - ${address.municipality} / ${address.department} ${address.reference ? `/ Referencia: ${address.reference}` : ''}`
    },

    refreshCustomerAddresses() {
      this.showAddressSidebar = false
      axios
        .get(`customer/${this.customerData.id}`)
        .then(response => {
          this.customerData = response.data.data
          this.$nextTick(() => { this.showAddressSelector = true })
        })
        .catch(error => { this.showErrors(error) })
    },

    saveData() {
      if (this.activeLines.length === 0) {
        this.makeToast('warning', 'Alerta', 'No se han agregado cantidades a ningún producto.')
        return
      }

      if (!this.customerOrderData.price_list_id) {
        this.makeToast('warning', 'Alerta', 'Debe seleccionar una lista de precios.')
        return
      }

      if (!this.customerOrderData.currency_id) {
        this.makeToast('warning', 'Alerta', 'Debe seleccionar una moneda.')
        return
      }

      if (!this.customerOrderData.user_id) {
        this.makeToast('warning', 'Alerta', 'Debe seleccionar un vendedor.')
        return
      }

      this.$bvModal
        .msgBoxConfirm('¿Deseas crear el pedido?', {
          title: 'Confirmar',
          size: 'sm',
          okVariant: 'primary',
          cancelVariant: 'outline-dark',
          okTitle: 'SI',
          cancelTitle: 'NO',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (!value) return

          this.isSavingOrder = true

          const payload = {
            ...this.customerOrderData,
            products: this.activeLines.map(line => ({
              product_id: line.productVariantId,
              quantity: line.cantidad,
              price: line.precioActual,
            })),
          }

          axios
            .post('customer-order', payload)
            .then(async response => {
              if (response.data.res) {
                this.makeToast('success', 'Nuevo pedido', `Se ha solicitado el pedido correctamente: #${response.data.data.Id}.`)
                this.isSavingOrder = false
                await new Promise(resolve => { window.setTimeout(resolve, 500) })
                this.$emit('addOrderTab', parseInt(response.data.data.Id, 10))
              } else {
                throw response
              }
            })
            .catch(error => {
              this.isSavingOrder = false
              this.showErrors(error)
            })
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

/*
  Encabezado del pedido: tarjeta de cliente (fondo azul claro) y tarjeta de
  detalles (blanca), con el azul del sistema en titulos e iconos.
*/
.order-header {
  margin-bottom: 1.5rem;

  &__client {
    background: #eef3f9;
    border: 1px solid #e3e8ef;
    border-radius: 10px;
    padding: 16px 18px;
    margin-bottom: 14px;
  }

  &__details {
    background: #fff;
    border: 1px solid #e3e8ef;
    border-radius: 10px;
    box-shadow: 0 4px 18px rgba(18, 64, 111, 0.06);
    padding: 16px 18px;
  }

  &__label {
    display: block;
    color: #3c4b64;
    font-weight: 600;
    font-size: 0.95rem;
    margin-bottom: 6px;
  }

  &__title {
    color: #12406f;
    font-weight: 700;
    margin-bottom: 14px;
  }

  &__customer-select .vs__dropdown-toggle {
    background: #fff;
  }

  &__customer {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #d3dce8;
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #dbe5f1;
    color: #12406f;
  }

  &__customer-body {
    min-width: 0;
  }

  &__customer-name {
    color: #12406f;
    font-weight: 700;
    margin-bottom: 4px;
    overflow-wrap: anywhere;
  }

  // NIT | Cel | Tel con separadores; se apilan si no caben.
  &__meta {
    display: flex;
    flex-wrap: wrap;
    row-gap: 2px;

    > span {
      padding-right: 12px;
      margin-right: 12px;
      border-right: 1px solid #d3dce8;
      white-space: nowrap;

      &:last-child {
        border-right: 0;
        margin-right: 0;
        padding-right: 0;
      }
    }
  }

  &__address {
    display: flex;
    align-items: flex-start;
    margin-top: 12px;
  }

  &__address-icon {
    color: #6e7891;
    margin: 2px 8px 0 0;
    flex: 0 0 auto;
  }

  &__address-body {
    min-width: 0;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    color: #12406f;
  }

  &__price-list .vs__dropdown-toggle {
    border-color: #a9c1dc;
  }
}

.dark-layout .order-header {
  &__client,
  &__details {
    background: #283046;
    border-color: #3b4253;
  }

  &__avatar {
    background: #3b4253;
    color: #d0d2d6;
  }

  &__title,
  &__customer-name,
  &__link,
  &__label {
    color: #d0d2d6;
  }

  &__customer,
  &__meta > span {
    border-color: #3b4253;
  }
}

.order-add-wrapper {
  .add-new-customer-header {
    padding: $options-padding-y $options-padding-x;
    color: $success;

    &:hover {
      background-color: rgba($success, 0.12);
    }
  }

  &__catalogo {
    max-height: 300px;
    overflow-y: auto;
    padding-right: 4px;
  }
}

.estilo-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fafafa;
  }

  &--added {
    border-color: #28c76f;
    background: #f0faf4;
  }

  &__thumb {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid #d8dee9;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__code {
    font-weight: 700;
    font-size: 13px;
    color: #1a3a5c;
  }

  &__action {
    flex-shrink: 0;
  }
}

.price-input {
  display: flex;
  align-items: center;
  gap: 6px;

  &__display {
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;

    &--locked {
      cursor: default;
      color: #1a3a5c;
    }
  }

  &__original {
    font-size: 10px;
    color: #b9b9c3;
    text-transform: uppercase;
  }

  &__badge--offer {
    background: #ffe6e6 !important;
    color: #ea5455 !important;
  }

  &__badge {
    font-size: 10px;
    background: #fff3e8;
    color: #ff9f43;
    padding: 1px 6px;
    border-radius: 10px;
    font-weight: 500;
  }

  &__field {
    width: 90px;
    padding: 2px 6px;
    border: 1px solid #1a3a5c;
    border-radius: 4px;
    font-size: 14px;
    outline: none;

    &:focus {
      box-shadow: 0 0 0 2px rgba(26, 58, 92, 0.2);
    }
  }
}

.order-grid {
  &__table {
    font-size: 12px;
    white-space: nowrap;
    margin-bottom: 0;

    th {
      font-size: 10px;
      text-transform: uppercase;
      color: #6e6b7b;
      font-weight: 600;
      padding: 6px 4px;
      text-align: center;
      background: #f8f9fa;
      border-bottom: 2px solid #1a3a5c;
    }

    td {
      vertical-align: middle;
      padding: 4px;
      border-bottom: 1px solid #ebe9f1;
    }
  }

  &__th-style {
    min-width: 120px;
    text-align: left !important;
  }

  &__th-size {
    min-width: 48px;
  }

  &__th-num {
    min-width: 55px;
  }

  &__th-price {
    min-width: 110px;
  }

  &__th-amount {
    min-width: 110px;
    text-align: right !important;
  }

  &__th-actions {
    width: 40px;
  }

  &__style-cell {
    background: #fafbfc;
    border-right: 1px solid #ebe9f1;
    padding-left: 8px !important;
  }

  &__qty-cell {
    text-align: center;
  }

  &__qty-input {
    width: 40px;
    height: 28px;
    text-align: center;
    border: 1px solid #d8dee9;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 0;
    background: #fff;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:focus {
      outline: none;
      border-color: #1a3a5c;
      box-shadow: 0 0 0 2px rgba(26, 58, 92, 0.15);
    }

    &--empty {
      background: #fafbfc;
      color: #b9b9c3;
      border-style: dashed;
    }

    &--max {
      border-color: #ff9f43;
      background: #fff8f0;
    }

    &--no-stock {
      background: #f5f5f5;
      color: #ccc;
      cursor: not-allowed;
      border-color: #e8e8e8;
    }
  }

  // Talla con oferta: precio de lista tachado y ofertado debajo de la cantidad.
  &__offer {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
    margin-top: 2px;
    font-size: 0.75rem;

    s {
      color: #82868b;
    }

    strong {
      color: #ea5455;
    }
  }

  &__stock-hint {
    display: block;
    font-size: 9px;
    color: #b9b9c3;
    margin-top: 1px;
  }

  &__row-total {
    white-space: nowrap;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 12px 8px;
    border-top: 2px solid #1a3a5c;
    font-size: 16px;
    font-weight: 600;
  }

  &__grand-total {
    color: #1a3a5c;
    font-size: 20px;
  }
}
</style>
